/**
 * Background classes for the initials avatar. Every entry is a Tailwind `*-700` shade that gives
 * WCAG AA contrast (4.5:1) with `text-white`; keep it that way when editing, the spec checks it.
 * Written out in full so Tailwind's source scan emits them.
 */
export const AVATAR_COLOR_PALETTE = [
  'bg-red-700',
  'bg-orange-700',
  'bg-amber-700',
  'bg-green-700',
  'bg-teal-700',
  'bg-cyan-700',
  'bg-sky-700',
  'bg-blue-700',
  'bg-indigo-700',
  'bg-violet-700',
  'bg-fuchsia-700',
  'bg-rose-700',
] as const;

const firstLetter = (value: string | null | undefined): string =>
  Array.from((value ?? '').trim())[0]?.toLocaleUpperCase() ?? '';

/**
 * Initials of a person: first letter of the name plus first letter of the surname, or a single
 * letter without surname. When the name is blank the first letter of `fallback` (a username, say)
 * stands in; blank everywhere gives `''`.
 */
export function avatarInitials(
  name: string | null | undefined,
  surname: string | null | undefined,
  fallback = '',
): string {
  const first = firstLetter(name);
  if (!first) return firstLetter(fallback);
  return first + firstLetter(surname);
}

/**
 * Background class for an avatar, derived only from `seed` (the account id), so the same account
 * gets the same colour in every session and on every device. 32-bit FNV-1a modulo the palette;
 * pair it with `text-white`.
 */
export function avatarColorClass(seed: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return AVATAR_COLOR_PALETTE[(hash >>> 0) % AVATAR_COLOR_PALETTE.length];
}

/**
 * Classes for the initials span of an avatar: the account's colour with white text, or a muted
 * style while the id is not known yet.
 */
export function avatarFallbackClass(seed: string | null | undefined): string {
  return seed ? `${avatarColorClass(seed)} text-white` : 'bg-muted text-muted-foreground';
}
